import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';

/* ════════════════════════════════════════════════════════════════════════════
   Napredni filter spiska zaposlenih

   Filtrira se NAD VEĆ UČITANIM spiskom — server vraća sve zaposlene firme
   jednim pozivom, pa svaka promena filtera radi trenutno, bez odlaska na bazu.
   Zato i Excel izvoz prati filter: izvozi se isto što se vidi.

   Brojači (ukupno / aktivni / pasivni) računaju se nad PUNIM spiskom, ne nad
   filtriranim — inače bi „ukupno" menjalo vrednost svaki put kad se nešto
   suzi, a to je broj koji korisniku treba kao stalna referenca.
   ════════════════════════════════════════════════════════════════════════════ */

export const PRAZAN_FILTER = {
    status: 'SVI',        // SVI | AKTIVAN | PASIVAN
    statusFirma: null,    // Prijavljen | Neprijavljen
    pozicije: [],         // vrednosti iz kolone „Pozicija" (+ „Dodatna pozicija")
    kategorije: [],       // slova iz kolone „Kategorije vozačke"
    imaDokument: [],      // ADR | CPC | TAHO | PASOS | VIZA
    nemaDokument: [],
    istice: null,         // broj dana (15 / 30 / 60 / 90)
};

/* 'dd.MM.yyyy' -> Date. Datumi stižu formatirani iz SP-a (konvencija projekta),
   pa se za računanje roka moraju razložiti — `new Date('31.12.2026')` ne radi. */
const uDatum = (s) => {
    if (!s) return null;
    const d = String(s).trim().split('.').filter(Boolean);
    if (d.length < 3) return null;
    const dan = +d[0], mesec = +d[1], godina = +d[2];
    if (!dan || !mesec || !godina) return null;
    return new Date(godina, mesec - 1, dan);
};

/* Kolone po dokumentu: broj (postoji li uopšte) i datum isteka. */
const DOKUMENTI = {
    ADR:   { broj: 'ADR broj',       istek: 'ADR isticanje' },
    CPC:   { broj: 'CPC broj',       istek: 'CPC isticanje' },
    TAHO:  { broj: 'Broj tahografa', istek: 'Isticanje tahografa' },
    PASOS: { broj: 'Broj pasoša',    istek: 'Isticanje pasoša' },
    VIZA:  { broj: 'Broj vize',      istek: 'Isticanje vize' },
};

const imaVrednost = (v) => v != null && String(v).trim() !== '' && String(v).trim() !== '-';

/* Kolone koje nose ROK DOKUMENTA. Namerno se NE hvata sve što liči na kraj
   nečega: „Kraj zaposlenja" i „Datum raskida" su kraj radnog odnosa, ne isticanje
   dokumenta — da su unutra, svaki zaposleni sa upisanim datumom prestanka
   iskakao bi u filteru „ističe u roku". */
const jeKolonaIsteka = (kolona) =>
    /isticanje/i.test(kolona) || kolona === 'Kraj osiguranja';

/* Jedan zaposleni prolazi filter samo ako zadovolji SVE postavljene uslove. */
export const primeniFilter = (redovi, f) => {
    if (!redovi) return redovi;
    const uslovi = f || PRAZAN_FILTER;

    return redovi.filter(r => {
        if (uslovi.status === 'AKTIVAN' && r.Status !== 'Aktivan') return false;
        if (uslovi.status === 'PASIVAN' && r.Status !== 'Pasivan') return false;

        if (uslovi.statusFirma && r['Status u firmi'] !== uslovi.statusFirma) return false;

        /* Pozicija se gleda i u dodatnoj: vozač koji je i serviser mora da se
           nađe i pod jednim i pod drugim. */
        if (uslovi.pozicije?.length) {
            const moje = [r['Pozicija'], r['Dodatna pozicija']].filter(imaVrednost);
            if (!moje.some(p => uslovi.pozicije.includes(p))) return false;
        }

        if (uslovi.kategorije?.length) {
            const moje = razloziKategorije(r['Kategorije vozačke']);
            if (!uslovi.kategorije.every(k => moje.includes(k))) return false;
        }

        if (uslovi.imaDokument?.length &&
            !uslovi.imaDokument.every(k => imaVrednost(r[DOKUMENTI[k]?.broj]))) return false;

        if (uslovi.nemaDokument?.length &&
            !uslovi.nemaDokument.every(k => !imaVrednost(r[DOKUMENTI[k]?.broj]))) return false;

        /* „Ističe u narednih N dana" gleda SVE datume isteka koje zaposleni ima.
           Već istekao dokument se takođe računa — rok koji je prošao je hitniji
           od onog koji tek dolazi, pa nema smisla da ispadne iz spiska. */
        if (uslovi.istice) {
            const granica = new Date();
            granica.setHours(0, 0, 0, 0);
            granica.setDate(granica.getDate() + Number(uslovi.istice));

            const bilo = Object.keys(r).some(kolona => {
                if (!jeKolonaIsteka(kolona)) return false;
                const d = uDatum(r[kolona]);
                return d && d <= granica;
            });
            if (!bilo) return false;
        }

        return true;
    });
};

/* 'B, C, CE' -> ['B','C','CE'] */
const razloziKategorije = (v) =>
    String(v || '').split(/[,;/\s]+/).map(x => x.trim().toUpperCase()).filter(Boolean);

const ZaposleniFilter = ({ sviRedovi, filter, postaviFilter, prikazano }) => {
    const { t } = useTranslation();
    const panelRef = useRef(null);
    const podaci = sviRedovi || [];

    /* Brojači nad punim spiskom — stalna referenca, ne menjaju se sa filterom. */
    const ukupno = podaci.length;
    const aktivni = podaci.filter(r => r.Status === 'Aktivan').length;
    const pasivni = ukupno - aktivni;

    /* Opcije se izvode IZ PODATAKA, ne iz šifarnika: svaka firma ima svoje
       pozicije i kategorije, pa fiksna lista kod jednih bude prazna a kod
       drugih nepotpuna. */
    const pozicijeOpcije = useMemo(() => {
        const skup = new Set();
        podaci.forEach(r => {
            [r['Pozicija'], r['Dodatna pozicija']].forEach(p => { if (imaVrednost(p)) skup.add(p); });
        });
        return [...skup].sort((a, b) => a.localeCompare(b, 'sr')).map(v => ({ label: v, value: v }));
    }, [podaci]);

    const kategorijeOpcije = useMemo(() => {
        const skup = new Set();
        podaci.forEach(r => razloziKategorije(r['Kategorije vozačke']).forEach(k => skup.add(k)));
        return [...skup].sort().map(v => ({ label: v, value: v }));
    }, [podaci]);

    const dokumentiOpcije = [
        { label: t('zapFilter.dokAdr'),   value: 'ADR' },
        { label: t('zapFilter.dokCpc'),   value: 'CPC' },
        { label: t('zapFilter.dokTaho'),  value: 'TAHO' },
        { label: t('zapFilter.dokPasos'), value: 'PASOS' },
        { label: t('zapFilter.dokViza'),  value: 'VIZA' },
    ];

    const isticeOpcije = [15, 30, 60, 90].map(d => ({ label: t('zapFilter.uDana', { broj: d }), value: d }));

    const izmeni = (delta) => postaviFilter({ ...filter, ...delta });

    /* Broj postavljenih uslova stoji na dugmetu — da se sa zatvorenim panelom
       vidi da filter uopšte radi. Bez toga korisnik gleda kratak spisak i ne
       zna zašto je kratak. */
    const brojUslova =
        (filter.status !== 'SVI' ? 1 : 0) +
        (filter.statusFirma ? 1 : 0) +
        (filter.pozicije?.length ? 1 : 0) +
        (filter.kategorije?.length ? 1 : 0) +
        (filter.imaDokument?.length ? 1 : 0) +
        (filter.nemaDokument?.length ? 1 : 0) +
        (filter.istice ? 1 : 0);

    const brojka = (kljuc, vrednost, boja) => (
        <button type="button"
            className={'zap-brojka' + (filter.status === kljuc ? ' zap-brojka--aktivna' : '')}
            onClick={() => izmeni({ status: filter.status === kljuc ? 'SVI' : kljuc })}>
            <span className="zap-brojka-broj" style={boja ? { color: boja } : undefined}>{vrednost}</span>
            <span className="zap-brojka-opis">
                {kljuc === 'SVI' ? t('zapFilter.ukupno')
                    : kljuc === 'AKTIVAN' ? t('zapFilter.aktivni') : t('zapFilter.neaktivni')}
            </span>
        </button>
    );

    /* Polja stoje u POPOVERU, ne u traci iznad tabele. Razlog je praktičan:
       otvoren panel u toku strane gurne tabelu naniže i ona pobegne ispod
       okvira, pa korisnik filtrira ono što ne vidi. Popover pluta preko
       sadržaja — tabela ostaje tačno gde jeste. */
    const polja = (
        <>
            {/* Brojači stoje NA VRHU panela: to su i podaci i prekidači, pa
                pripadaju filteru, a ne traci zaglavlja koja i bez njih ima
                dovoljno sadržaja. */}
            <div className="zap-brojke">
                {brojka('SVI', ukupno)}
                {brojka('AKTIVAN', aktivni, '#2e7d4f')}
                {brojka('PASIVAN', pasivni, '#b83a3a')}
            </div>

        <div className="zap-filter-polja">
            <div className="zap-polje">
                <label>{t('zapFilter.statusFirma')}:</label>
                <Dropdown value={filter.statusFirma} style={{ width: '100%' }} showClear
                    options={[
                        { label: t('zapFilter.prijavljen'), value: 'Prijavljen' },
                        { label: t('zapFilter.neprijavljen'), value: 'Neprijavljen' },
                    ]}
                    placeholder={t('zapFilter.sve')}
                    onChange={e => izmeni({ statusFirma: e.value ?? null })} />
            </div>

            <div className="zap-polje">
                <label>{t('zapFilter.pozicija')}:</label>
                <MultiSelect value={filter.pozicije} options={pozicijeOpcije} display="chip"
                    style={{ width: '100%' }} filter maxSelectedLabels={2}
                    placeholder={t('zapFilter.sve')}
                    onChange={e => izmeni({ pozicije: e.value })} />
            </div>

            <div className="zap-polje">
                <label>{t('zapFilter.kategorije')}:</label>
                <MultiSelect value={filter.kategorije} options={kategorijeOpcije} display="chip"
                    style={{ width: '100%' }} maxSelectedLabels={4}
                    placeholder={t('zapFilter.sve')}
                    onChange={e => izmeni({ kategorije: e.value })} />
            </div>

            <div className="zap-polje">
                <label>{t('zapFilter.ima')}:</label>
                <MultiSelect value={filter.imaDokument} options={dokumentiOpcije} display="chip"
                    style={{ width: '100%' }} maxSelectedLabels={2}
                    placeholder={t('zapFilter.bilo')}
                    onChange={e => izmeni({ imaDokument: e.value })} />
            </div>

            <div className="zap-polje">
                <label>{t('zapFilter.nema')}:</label>
                <MultiSelect value={filter.nemaDokument} options={dokumentiOpcije} display="chip"
                    style={{ width: '100%' }} maxSelectedLabels={2}
                    placeholder={t('zapFilter.bilo')}
                    onChange={e => izmeni({ nemaDokument: e.value })} />
            </div>

            <div className="zap-polje">
                <label>{t('zapFilter.istice')}:</label>
                <Dropdown value={filter.istice} options={isticeOpcije} style={{ width: '100%' }} showClear
                    placeholder={t('zapFilter.bilo')}
                    onChange={e => izmeni({ istice: e.value ?? null })} />
            </div>
        </div>
        </>
    );

    return (
        <div className="zap-filter">
            <div className="zap-filter-akcije">
                {brojUslova > 0 && (
                    <span className="zap-filter-prikaz">
                        {t('zapFilter.prikazano', { broj: prikazano, ukupno })}
                    </span>
                )}
                <Button type="button" icon="pi pi-filter" className="p-button-sm"
                    label={brojUslova > 0 ? `${t('zapFilter.naslov')} (${brojUslova})` : t('zapFilter.naslov')}
                    onClick={(e) => panelRef.current?.toggle(e)} />
                {brojUslova > 0 && (
                    <Button type="button" icon="pi pi-filter-slash" className="p-button-sm p-button-text"
                        label={t('zapFilter.ponisti')} onClick={() => postaviFilter(PRAZAN_FILTER)} />
                )}
            </div>

            {/* `dismissable={false}` je namerno: padajuće liste unutar panela
                (MultiSelect, Dropdown) iscrtavaju se IZVAN njegovog DOM-a, pa
                bi zatvaranje na klik napolje gasilo ceo filter čim se otvori
                bilo koja lista. Zato se zatvara krstićem. */}
            <OverlayPanel ref={panelRef} className="zap-filter-panel" showCloseIcon dismissable={false}>
                {polja}
            </OverlayPanel>
        </div>
    );
};

export default ZaposleniFilter;
